'use client';
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useState, useEffect, useCallback } from 'react';
import paymentService, {
  InvoiceResponse,
  InstallmentOption,
  BankAccount,
} from '@/services/PaymentService';
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext';
import { clearReservationCookies } from '@/lib/cookies';
import CountryCitySelect from '@/components/ui/CountryCitySelect';
import { useCountries } from '@/contexts/CountriesContext';
import type { Country } from '@/contexts/CountriesContext';
import { PaymentCardForm, PaymentBankTransferForm, PartialPaymentAmountBlock } from '@/components/payment';
import type { PartialPaymentContext } from '@/types/partial-payment';
import { formatCurrencyAmount, type CurrencyForFormat } from '@/lib/formatCurrency';

/**
 * Sağ barda yer kazanmak için kalem açıklamasından ürün adı ve paket tekrarını kaldırır.
 * Fatura belgesi/PDF etkilenmez; sadece ödeme sayfası sidebar görüntüsü kısaltılır.
 */
function getSidebarItemLabel(
  fullDescription: string,
  productName?: string | null,
): string {
  let short = fullDescription.trim();
  if (productName && productName.length > 0 && short.startsWith(productName)) {
    short = short.slice(productName.length).trim();
  }
  // "[STANDARD PACKAGE] - " veya " - [PACKAGE] - " kalıbını kaldır
  const bracketMatch = short.match(/^\s*-\s*\[\s*[^\]]+\]\s*-\s*(.*)/);
  if (bracketMatch) {
    short = bracketMatch[1].trim();
  } else {
    const leadingBracket = short.match(/^\s*\[\s*[^\]]+\]\s*-\s*(.*)/);
    if (leadingBracket) {
      short = leadingBracket[1].trim();
    }
  }
  short = short.replace(/^\s*-\s*/, '').trim();
  return short.length > 0 ? short : fullDescription;
}

// Status badge colors
const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
  partially_paid: 'bg-blue-100 text-blue-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-red-100 text-red-800',
};

interface InvoicePaymentProps {
  token?: string;
  translations?: Record<string, string>;
}

export default function InvoicePayment({ token: propToken, translations = {} }: InvoicePaymentProps) {
  const { getVariable, language: currentLocale } = useLanguageVariables();
  
  // Get token from props or URL
  const [token, setToken] = useState<string | null>(propToken || null);

  useEffect(() => {
    // Önce prop'tan token'ı al
    if (propToken) {
      setToken(propToken);
      return;
    }
    
    // Prop'tan yoksa URL'den al
    if (typeof window !== 'undefined') {
      const pathParts = window.location.pathname.split('/').filter(p => p);
      // URL pattern: /invoice-payment/{token} veya /pay/{token}
      const invoicePaymentIndex = pathParts.findIndex(p => p === 'invoice-payment' || p === 'pay' || p === 'odeme');
      if (invoicePaymentIndex !== -1 && pathParts[invoicePaymentIndex + 1]) {
        setToken(pathParts[invoicePaymentIndex + 1]);
      } else if (pathParts.length > 0) {
        // Son segment token olabilir
        const lastSegment = pathParts[pathParts.length - 1];
        // Token genellikle uzun bir string (örn: zunqu65atV5sw2TjZEQriGdFe9TSe3bsKfHxrKOKGDCdNmNL3pbG5gmjvKOu8wQa)
        if (lastSegment && lastSegment.length > 20) {
          setToken(lastSegment);
        }
      }
    }
  }, [propToken]);

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoiceData, setInvoiceData] = useState<InvoiceResponse | null>(null);
  // Tabs mantığı: varsayılan olarak kredi kartı (eğer varsa), yoksa banka havalesi
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'bank' | null>(null);
  const [processing, setProcessing] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Card form state
  const [cardHolder, setCardHolder] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expireMonth, setExpireMonth] = useState('');
  const [expireYear, setExpireYear] = useState('');
  const [cvc, setCvc] = useState('');
  const [installment, setInstallment] = useState(1);
  const [installments, setInstallments] = useState<InstallmentOption[]>([]);
  const [use3D, setUse3D] = useState(true);

  // Bank transfer form state
  const [selectedBank, setSelectedBank] = useState<number | null>(null);
  const [transferAmount, setTransferAmount] = useState('');
  const [transferDate, setTransferDate] = useState('');
  const [senderName, setSenderName] = useState('');
  const [senderBank, setSenderBank] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [partialPaymentContext, setPartialPaymentContext] = useState<PartialPaymentContext | null>(null);
  const [wantsPartialPayment, setWantsPartialPayment] = useState(false);
  const [paymentSchedule, setPaymentSchedule] = useState<NonNullable<InvoiceResponse['payment_schedule']>>([]);
  const [payNowAmount, setPayNowAmount] = useState('');

  // 3D Secure HTML
  const [threeDSHtml, setThreeDSHtml] = useState<string | null>(null);

  // Billing info form (eksik fatura bilgisi tamamlama)
  const [billingType, setBillingType] = useState<'individual' | 'corporate'>('individual');
  const [billingFirstName, setBillingFirstName] = useState('');
  const [billingLastName, setBillingLastName] = useState('');
  const [billingCompanyName, setBillingCompanyName] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingCountry, setBillingCountry] = useState('');
  const [billingTaxNumber, setBillingTaxNumber] = useState('');
  const [billingTaxOffice, setBillingTaxOffice] = useState('');
  const [billingTcNo, setBillingTcNo] = useState('');
  const [billingSubmitting, setBillingSubmitting] = useState(false);
  const [selectedBillingAddressId, setSelectedBillingAddressId] = useState<number | ''>('');
  const countries = useCountries();

  // Translation helper
  const t = (key: string, fallback: string) => {
    return translations[key] || getVariable(key, fallback);
  };

  // Validation helpers
  const validateBankTransferForm = (): string | null => {
    // Bank selection check
    if (!selectedBank) {
      return t('payment.error.select_bank', 'Please select the bank for transfer');
    }

    // Amount validation
    const amount = parseFloat(transferAmount);
    if (!transferAmount || isNaN(amount) || amount <= 0) {
      return t('payment.error.invalid_amount', 'Enter a valid amount');
    }

    const remainingAmount = invoiceData?.invoice?.remaining_amount || 0;
    const minAmount = partialPaymentContext?.enabled && partialPaymentContext.bounds
      ? partialPaymentContext.bounds.min_amount
      : 0;
    if (amount > remainingAmount) {
      const currencySymbol = invoiceData?.invoice?.currency?.symbol || '₺';
      return t('payment.error.amount_exceeds', `Amount cannot exceed the remaining invoice amount (${remainingAmount.toFixed(2)} ${currencySymbol})`);
    }
    if (minAmount > 0 && amount + 0.001 < minAmount) {
      return t('payment.error.amount_below_min', `Minimum amount is ${minAmount.toFixed(2)}`);
    }

    // Date validation
    if (!transferDate) {
      return t('payment.error.transfer_date_required', 'Enter the transfer date');
    }

    const today = new Date().toISOString().split('T')[0];
    if (transferDate > today) {
      return t('payment.error.invalid_date', 'Transfer date cannot be in the future');
    }

    // Sender name check
    if (!senderName || senderName.trim().length === 0) {
      return t('payment.error.sender_name_required', 'Enter the sender name');
    }

    // Email validation
    if (!senderEmail) {
      return t('payment.error.email_required', 'Enter the email address');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(senderEmail)) {
      return t('payment.error.invalid_email', 'Enter a valid email address');
    }

    // Phone validation (optional but format check)
    if (senderPhone && !/^[\d\s\-\+\(\)]+$/.test(senderPhone)) {
      return t('payment.error.invalid_phone', 'Enter a valid phone number');
    }

    // Receipt file validation
    if (receiptFile) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (receiptFile.size > maxSize) {
        return t('payment.error.file_too_large', 'File size must be at most 5MB');
      }

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(receiptFile.type)) {
        return t('payment.error.invalid_file_type', 'Only JPG, PNG or PDF files are allowed');
      }
    }

    return null; // Validation successful
  };

  // Form reset helper
  const resetBankTransferForm = () => {
    setSelectedBank(null);
    setTransferAmount('');
    setTransferDate('');
    setSenderName('');
    setSenderBank('');
    setReferenceNo('');
    setSenderEmail('');
    setSenderPhone('');
    setNotes('');
    setReceiptFile(null);
  };
  
  // 3D Secure callback handler
  // NOT: t fonksiyonu dependency'de OLMAMALI - her render'da yeni referans alır, handle3DCallback sürekli
  // değişir ve 3D callback useEffect her render'da tetiklenir. getVariable kullanıyoruz.
  const handle3DCallback = useCallback(async (callbackParams: Record<string, string | null>) => {
    if (!token) return;
    const tr = (key: string, fb: string) => translations[key] || getVariable(key, fb);

    try {
      setProcessing(true);
      setError(null);

      const result = await paymentService.handle3DCallback(token, callbackParams);

      if (result.success) {
        setSuccess(true);
        setSuccessMessage(tr('payment.success.completed', 'Your payment has been completed successfully!'));
        clearReservationCookies();
        const data = await paymentService.getInvoice(token);
        setInvoiceData(data);
      } else {
        const code = (result as { error_code?: string }).error_code;
        let errorMsg = result.error_message || tr('payment.error.failed', 'Payment failed');
        if (code) {
          const translated = tr(`payment.garanti.error.${code}`, '');
          errorMsg = translated || tr('payment.garanti.error.default', errorMsg);
        }
        setError(errorMsg);
      }
    } catch (err) {
      let errorMsg = err instanceof Error ? err.message : tr('payment.error.failed', 'Payment failed');
      const code = (err as Error & { error_code?: string }).error_code;
      if (code) {
        const translated = tr(`payment.garanti.error.${code}`, '');
        errorMsg = translated || tr('payment.garanti.error.default', errorMsg);
      }
      setError(errorMsg);
    } finally {
      setProcessing(false);
    }
  }, [token, translations, getVariable]);
  
  // 3D Secure callback parametrelerini kontrol et (iyzico callback'inden döndükten sonra)
  useEffect(() => {
    if (typeof window === 'undefined' || !token) return;
    
    // URL'deki callback parametrelerini kontrol et (iyzico callback'inden döndükten sonra)
    const urlParams = new URLSearchParams(window.location.search);
    const callbackParams = {
      status: urlParams.get('status'),
      paymentId: urlParams.get('paymentId'),
      conversationId: urlParams.get('conversationId'),
      conversationData: urlParams.get('conversationData'),
      mdStatus: urlParams.get('mdStatus'),
    };
    
    // Eğer callback parametreleri varsa, callback'i işle
    if (callbackParams.conversationId && callbackParams.paymentId) {
      handle3DCallback(callbackParams);
      
      // URL'den callback parametrelerini temizle
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [token, handle3DCallback]);

  // Garanti 3D callback yönlendirmesi: URL'de payment_result varsa sonucu göster (token'dan bağımsız çalışır, ilk yüklemede da çalışsın diye)
  // payment_error_code varsa mesaj site diline göre çevrilir (payment.garanti.error.XXXX)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const result = params.get('payment_result');
    const message = params.get('payment_message');
    const errorCode = params.get('payment_error_code');
    if (result === 'success') {
      setSuccess(true);
      setSuccessMessage(t('payment.success.completed', 'Your payment has been completed successfully!'));
      clearReservationCookies();
      window.history.replaceState({}, '', window.location.pathname);
      // Fatura verisini güncelle (ödeme sonrası kalan tutar vb.)
      if (token) {
        paymentService.getInvoice(token).then((data) => setInvoiceData(data)).catch(() => {});
      }
    } else if (result === 'failed') {
      let errMsg = message ? decodeURIComponent(message) : t('payment.error.failed', 'Payment failed');
      if (errorCode) {
        const translated = t(`payment.garanti.error.${errorCode}`, '');
        if (translated) errMsg = translated;
      }
      setError(errMsg);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [token]); // token değişince de tekrar kontrol et (path sonradan parse edilmişse)

  // Fetch invoice data
  useEffect(() => {
    const fetchInvoice = async () => {
      if (!token) return;
      
      try {
        setLoading(true);
        const data = await paymentService.getInvoice(token);
        setInvoiceData(data);

        if (data.invoice?.customer_email) {
          setSenderEmail(data.invoice.customer_email);
        }
        if (data.invoice?.customer_name) {
          setSenderName(data.invoice.customer_name);
        }
        // Eksik fatura bilgisi formu için mevcut değerleri doldur (varsa)
        if (data.invoice && typeof data.invoice === 'object') {
          const inv = data.invoice as {
            billing_type?: 'individual' | 'corporate';
            customer_address?: { address?: string; city?: string; country?: string; first_name?: string; last_name?: string; company_name?: string };
            customer_tax_number?: string;
            customer_tax_office?: string;
          };
          setBillingType(inv.billing_type === 'corporate' ? 'corporate' : 'individual');
          const addr = inv.customer_address;
          if (addr && typeof addr === 'object') {
            setBillingFirstName(addr.first_name || '');
            setBillingLastName(addr.last_name || '');
            setBillingCompanyName(addr.company_name || '');
            setBillingAddress(addr.address || '');
            setBillingCity(addr.city || '');
            setBillingCountry(addr.country || '');
          }
          setBillingTaxNumber(inv.customer_tax_number || '');
          setBillingTaxOffice(inv.customer_tax_office || '');
          // Individual + Turkey için TC customer_tax_number'da tutulabilir
          if (inv.billing_type === 'individual' && inv.customer_address?.country === 'TR') {
            setBillingTcNo(inv.customer_tax_number || '');
          }
        }
        if (data.partial_payment) {
          setPartialPaymentContext(data.partial_payment);
        } else {
          setPartialPaymentContext(null);
        }
        setPaymentSchedule(Array.isArray(data.payment_schedule) ? data.payment_schedule : []);
        if (data.invoice?.remaining_amount) {
          const rem = data.invoice.remaining_amount.toString();
          setTransferAmount(rem);
          if (data.partial_payment?.enabled) {
            const suggested = data.partial_payment?.suggested_pay_amount;
            setPayNowAmount(
              typeof suggested === 'number' && suggested > 0 ? String(suggested) : rem,
            );
          } else {
            setPayNowAmount('');
          }
        }

        // Varsayılan payment method'u belirle (kredi kartı varsa o, yoksa banka havalesi)
        const hasCard = data.payment_methods?.some((m: any) => {
          if (typeof m === 'string') return m === 'card' || m === 'credit_card';
          return m?.key === 'card' || m?.key === 'credit_card';
        });
        const hasBank = data.payment_methods?.some((m: any) => {
          if (typeof m === 'string') return m === 'bank' || m === 'bank_transfer';
          return m?.key === 'bank_transfer' || m?.key === 'bank';
        });
        
        if (hasCard) {
          setPaymentMethod('card');
        } else if (hasBank) {
          setPaymentMethod('bank');
        }

        const list = Array.isArray(data.installments) && data.installments.length > 0
          ? data.installments
          : [{ installmentNumber: 1, installmentPrice: data.invoice.remaining_amount, totalPrice: data.invoice.remaining_amount }];
        setInstallments(list);

        // Test modu aktifse ve Garanti değilse (iyzico) test kartını otomatik yükle; Garanti’de dropdown kullanılır
        if (data.test_mode && hasCard && data.payment_provider_key !== 'garanti_pos') {
          loadTestCard();
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : t('payment.error.generic', 'An error occurred'));
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [token]);

  useEffect(() => {
    if (!token || paymentMethod !== 'card') {
      return;
    }
    const digits = cardNumber.replace(/\s/g, '');
    if (digits.length < 6) {
      return;
    }
    const remaining = invoiceData?.invoice?.remaining_amount ?? 0;
    const bounds = partialPaymentContext?.enabled && wantsPartialPayment ? partialPaymentContext.bounds : undefined;
    let amount = remaining;
    if (bounds && payNowAmount.trim() !== '') {
      const n = parseFloat(payNowAmount);
      if (!Number.isNaN(n) && n > 0) {
        amount = n;
      }
    }
    if (!(amount > 0)) {
      return;
    }
    const bin = digits.slice(0, 6);
    let cancelled = false;
    paymentService
      .getInstallments(token, bin, amount)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data.installments) && data.installments.length > 0 ? data.installments : [];
        setInstallments(list);
        setInstallment(1);
      })
      .catch(() => {
        if (!cancelled) {
          setInstallments([]);
          setInstallment(1);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, paymentMethod, cardNumber, payNowAmount, partialPaymentContext, invoiceData?.invoice?.remaining_amount]);

  // Test kartını yükle (iyzico test kartları)
  // iyzico dokümantasyonuna göre: https://docs.iyzico.com/ek-bilgiler/test-kartlari
  // SKT ve CVV değerleri random olabilir, ancak doğru formatta ve SKT günümüzden ileri bir tarih olmalıdır
  const loadTestCard = useCallback(() => {
    // iyzico test kartları (başarılı yanıt veren kartlar)
    const testCards = [
      { number: '5890040000000016', holder: 'TEST KART', month: '12', year: '30', cvc: '123' }, // Akbank Master Card Debit
      { number: '5526080000000006', holder: 'TEST KART', month: '12', year: '30', cvc: '123' }, // Akbank Master Card Credit
      { number: '4766620000000001', holder: 'TEST KART', month: '12', year: '30', cvc: '123' }, // Denizbank Visa Debit
      { number: '4603450000000000', holder: 'TEST KART', month: '12', year: '30', cvc: '123' }, // Denizbank Visa Credit
      { number: '5400360000000003', holder: 'TEST KART', month: '12', year: '30', cvc: '123' }, // Garanti Master Card Credit
    ];
    
    // Rastgele bir test kartı seç
    const randomCard = testCards[Math.floor(Math.random() * testCards.length)];
    
    // Gelecek yılı hesapla (en az 2 yıl ileri)
    const futureYear = new Date().getFullYear() + 2;
    const lastTwoDigits = futureYear.toString().slice(-2);
    
    // Rastgele ay (01-12) ve CVC (100-999)
    const randomMonth = String(Math.floor(Math.random() * 12) + 1).padStart(2, '0');
    const randomCvc = String(Math.floor(Math.random() * 900) + 100);
    
    setCardNumber(randomCard.number);
    setCardHolder(randomCard.holder);
    setExpireMonth(randomMonth);
    setExpireYear(lastTwoDigits);
    setCvc(randomCvc);
  }, []);

  // Taksit isteğinin aynı (token, bin) ile kısa sürede tekrar atılmasını önle (iki effect’in aynı anda tetiklemesi)
  // Handle card payment
  const handleCardPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    const invoice = invoiceData?.invoice;
    const needsBillingInfo = invoice && (invoice as { billing_info_complete?: boolean }).billing_info_complete === false;
    const customerBillingAddresses = (invoice as { customer_billing_addresses?: unknown[] })?.customer_billing_addresses ?? [];
    const hasSavedAddresses = Array.isArray(customerBillingAddresses) && customerBillingAddresses.length > 0;

    if (needsBillingInfo && hasSavedAddresses && !selectedBillingAddressId) {
      setError(t('payment.error.billing_address_required', 'BillingAddress is required'));
      return;
    }
    if (needsBillingInfo && hasSavedAddresses && selectedBillingAddressId) {
      setProcessing(true);
      setError(null);
      try {
        const result = await paymentService.updateBillingInfo(token, { billing_address_id: Number(selectedBillingAddressId) });
        setInvoiceData((prev) => (prev ? { ...prev, invoice: result.invoice } : null));
      } catch (err) {
        setError(err instanceof Error ? err.message : t('payment.error.billing_update_failed', 'Billing information could not be updated'));
        setProcessing(false);
        return;
      }
    }

    const partialBounds = partialPaymentContext?.enabled && wantsPartialPayment ? partialPaymentContext.bounds : undefined;
    const remainingForPay = invoiceData?.invoice?.remaining_amount ?? 0;
    let cardAmount = remainingForPay;
    if (partialBounds) {
      const n = parseFloat(payNowAmount);
      if (!payNowAmount || Number.isNaN(n) || n <= 0) {
        setError(t('payment.error.invalid_amount', 'Enter a valid amount'));
        return;
      }
      if (n + 0.001 < partialBounds.min_amount) {
        setError(t('payment.error.amount_below_min', `Minimum amount is ${partialBounds.min_amount.toFixed(2)}`));
        return;
      }
      if (n > partialBounds.max_amount + 0.001) {
        setError(t('payment.error.amount_exceeds', 'Amount exceeds the maximum allowed'));
        return;
      }
      cardAmount = n;
    }

    setProcessing(true);
    setError(null);

    try {
      const result = await paymentService.payWithCard(token, {
        card_holder_name: cardHolder,
        card_number: cardNumber.replace(/\s/g, ''),
        expire_month: expireMonth,
        expire_year: expireYear,
        cvc,
        installment,
        use_3d: use3D,
        ...(cardAmount > 0 && partialBounds ? { amount: cardAmount } : {}),
        ...(currentLocale ? { locale: currentLocale } : {}),
      });

      if (result.requires_3d && result.html_content) {
        setThreeDSHtml(result.html_content);
      } else if (result.success) {
        setSuccess(true);
        setSuccessMessage(t('payment.success.completed', 'Your payment has been completed successfully!'));
        clearReservationCookies();
      }
    } catch (err) {
      let errorMsg = err instanceof Error ? err.message : t('payment.error.failed', 'Payment failed');
      const errorCode = (err as Error & { error_code?: string }).error_code;
      if (errorCode) {
        const translated = t(`payment.garanti.error.${errorCode}`, '');
        if (translated) errorMsg = translated;
        else errorMsg = t('payment.garanti.error.default', errorMsg);
      } else if (errorMsg.includes('BillingAddress') || errorMsg.includes('billing_address')) {
        errorMsg = t('payment.error.billing_address_required', 'BillingAddress is required');
      }
      setError(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  // Handle bank transfer
  const handleBankTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setError(t('payment.error.invalid_token', 'Invalid invoice token'));
      return;
    }

    // Merkezi validasyon
    const validationError = validateBankTransferForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const result = await paymentService.createBankTransfer(token, {
        bank_account_id: selectedBank!,
        amount: parseFloat(transferAmount),
        transfer_date: transferDate,
        sender_name: senderName.trim(),
        sender_bank: senderBank.trim() || undefined,
        reference_no: referenceNo.trim() || undefined,
        sender_email: senderEmail.trim(),
        sender_phone: senderPhone.trim() || undefined,
        notes: notes.trim() || undefined,
      });

      // Dekont yükleme (varsa)
      if (receiptFile && result.notification_id) {
        setUploadingReceipt(true);
        try {
          await paymentService.uploadReceipt(token, result.notification_id, receiptFile);
        } catch (uploadErr) {
          // Dekont yükleme hatası bildirimi oluşturmayı engellemez
          // Kullanıcıya bilgi ver (ama başarı mesajını göster)
          setSuccessMessage(
            t('payment.success.bank_transfer_with_receipt_error', 
              'Your transfer notification has been received but the receipt could not be uploaded. Please try again later.')
          );
        } finally {
          setUploadingReceipt(false);
        }
      }

      // Başarılı - formu temizle
      resetBankTransferForm();
      setSuccess(true);
      setSuccessMessage(t('payment.success.bank_transfer', 'Your transfer notification has been received. You will be notified by email when approved.'));
      clearReservationCookies();
    } catch (err) {
      // Backend'den gelen validation error'ları parse et
      let errorMessage = t('payment.error.bank_transfer', 'Transfer notification could not be created');
      
      if (err instanceof Error) {
        try {
          // JSON error response kontrolü
          const errorText = err.message;
          if (errorText.includes('errors') || errorText.includes('error')) {
            const errorData = JSON.parse(errorText);
            if (errorData.errors) {
              const firstError = Object.values(errorData.errors)[0];
              errorMessage = Array.isArray(firstError) ? firstError[0] : String(firstError);
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          } else {
            errorMessage = errorText;
          }
        } catch {
          // Parse edilemezse direkt mesajı kullan
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  // 3D Secure form auto-submit - useEffect hook must be at top level (Rules of Hooks)
  useEffect(() => {
    if (threeDSHtml) {
      // iyzico 3D Secure HTML'i genellikle otomatik submit olan bir form içerir
      // Script'lerin çalışması için HTML'i render ettikten sonra form submit etmeyi dene
      const attemptSubmit = () => {
        // Birden fazla selector dene
        const selectors = [
          'form[action*="3dsecure"]',
          'form[action*="3d"]',
          'form[method="post"]',
          '#iyzico-3ds-form form',
          'form',
        ];
        
        for (const selector of selectors) {
          const form = document.querySelector(selector) as HTMLFormElement;
          if (form && form.action) {
            // Form bulundu, submit et
            form.submit();
            return true;
          }
        }
        return false;
      };
      
      // Hemen dene
      if (attemptSubmit()) {
        return;
      }
      
      // HTML render olması için biraz bekle ve tekrar dene
      const timers = [
        setTimeout(attemptSubmit, 200),
        setTimeout(attemptSubmit, 500),
        setTimeout(attemptSubmit, 1000),
      ];
      
      return () => {
        timers.forEach(timer => clearTimeout(timer));
      };
    }
  }, [threeDSHtml]);

  // 3D Secure iframe render
  if (threeDSHtml) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg border border-neutral-200/90 p-6 w-full max-w-4xl">
          <h2 className="text-xl font-semibold mb-4 text-center">{t('payment.3ds.title', '3D Secure Verification')}</h2>
          <div className="flex items-center justify-center mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="ml-3 text-gray-600">{t('payment.3ds.redirecting', 'Redirecting to bank page...')}</p>
          </div>
          <div 
            id="iyzico-3ds-form"
            className="w-full" 
            dangerouslySetInnerHTML={{ __html: threeDSHtml }}
            style={{ minHeight: '400px', display: 'none' }}
          />
          <div className="text-center text-sm text-gray-500 mt-4">
            {t('payment.3ds.wait', 'Redirecting, please wait...')}
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg border border-neutral-200/90 p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('payment.success.title', 'Transaction Successful')}</h2>
          <p className="text-gray-600 mb-6">{successMessage}</p>
          {invoiceData?.invoice && (
            <div className="text-sm text-gray-500">
              {t('payment.invoice_no', 'Invoice No')}: {invoiceData.invoice.invoice_no}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Error state (no data)
  if (error && !invoiceData) {
    // BillingAddress hatasını kontrol et ve çeviri kullan
    let errorMessage = error;
    if (error.includes('BillingAddress') || error.includes('billing_address')) {
      errorMessage = t('payment.error.billing_address_required', 'BillingAddress is required');
    }
    
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg border border-neutral-200/90 p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('payment.error.title', 'Error')}</h2>
          <p className="text-gray-600">{errorMessage}</p>
        </div>
      </div>
    );
  }

  // Not payable
  if (invoiceData && !invoiceData.payable) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg border border-neutral-200/90 p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{invoiceData.message || t('payment.not_payable', 'This invoice is not payable')}</h2>
          <p className="text-gray-600">{t('payment.invoice_no', 'Invoice No')}: {invoiceData.invoice.invoice_no}</p>
        </div>
      </div>
    );
  }

  const invoice = invoiceData?.invoice;
  const bankAccounts = invoiceData?.bank_accounts || [];
  const needsBillingInfo = invoice && invoice.billing_info_complete === false;
  const customerBillingAddresses = (invoice as { customer_billing_addresses?: Array<{ id: number; type: string; title?: string; first_name?: string; last_name?: string; company_name?: string; address?: string; city?: string; country?: string }> })?.customer_billing_addresses ?? [];
  const hasSavedAddresses = Array.isArray(customerBillingAddresses) && customerBillingAddresses.length > 0;
  const missingFields = invoice?.missing_billing_fields || [];
  // Fatura bilgisi eksikse: kayıtlı adres varsa select göster + ödeme yöntemleri; yoksa sadece form
  const showOnlyBillingForm = needsBillingInfo && !hasSavedAddresses;
  const showBillingAddressSelect = needsBillingInfo && hasSavedAddresses;

  // Kayıtlı fatura adresi seçildiğinde uygula
  const handleApplySelectedBillingAddress = async () => {
    if (!token || !selectedBillingAddressId) return;
    setBillingSubmitting(true);
    setError(null);
    try {
      const result = await paymentService.updateBillingInfo(token, { billing_address_id: Number(selectedBillingAddressId) });
      setInvoiceData({ ...invoiceData!, invoice: result.invoice, payable: invoiceData!.payable, payment_methods: invoiceData!.payment_methods, bank_accounts: invoiceData!.bank_accounts });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('payment.error.billing_update_failed', 'Billing information could not be updated'));
    } finally {
      setBillingSubmitting(false);
    }
  };

  // Billing info form submit
  const handleBillingInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setBillingSubmitting(true);
    setError(null);
    try {
      const taxNum = billingType === 'corporate' ? billingTaxNumber.trim() : (billingCountry === 'TR' ? billingTcNo.trim() : '');
      const result = await paymentService.updateBillingInfo(token, {
        billing_type: billingType,
        customer_address: {
          address: billingAddress.trim(),
          city: billingCity.trim(),
          country: billingCountry.trim(),
          first_name: billingType === 'individual' ? billingFirstName.trim() : undefined,
          last_name: billingType === 'individual' ? billingLastName.trim() : undefined,
          company_name: billingType === 'corporate' ? billingCompanyName.trim() : undefined,
        },
        customer_tax_number: taxNum || undefined,
        customer_tax_office: billingType === 'corporate' ? billingTaxOffice.trim() || undefined : undefined,
      });
      setInvoiceData({ ...invoiceData!, invoice: result.invoice, payable: invoiceData!.payable, payment_methods: invoiceData!.payment_methods, bank_accounts: invoiceData!.bank_accounts });
      // Clear error on success
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('payment.error.billing_update_failed', 'Billing information could not be updated'));
    } finally {
      setBillingSubmitting(false);
    }
  };
  const balanceDue = invoice?.remaining_amount ?? invoice?.total_amount ?? 0;
  const partialOffered = Boolean(partialPaymentContext?.enabled && partialPaymentContext.bounds);
  const partialBounds = partialOffered && wantsPartialPayment ? partialPaymentContext!.bounds : undefined;
  const parsedPayNow = parseFloat(payNowAmount);
  const cardChargeBase =
    partialBounds && payNowAmount && !Number.isNaN(parsedPayNow) && parsedPayNow > 0
      ? parsedPayNow
      : balanceDue;
  const installmentChargeBase = partialBounds ? cardChargeBase : balanceDue;
  const selectedInstallmentTotal =
    installments.length > 0
      ? (installments.find((i) => i.installmentNumber === installment)?.totalPrice ?? installmentChargeBase)
      : installmentChargeBase;
  const cardCommissionAmount = Math.max(0, Math.round((selectedInstallmentTotal - installmentChargeBase) * 100) / 100);
  const cardCommissionRate =
    installmentChargeBase > 0 && cardCommissionAmount > 0
      ? Math.round((cardCommissionAmount / installmentChargeBase) * 1000) / 10
      : 0;
  const payableAmount =
    paymentMethod === 'card'
      ? partialBounds
        ? installment > 1 && installments.length > 0
          ? selectedInstallmentTotal
          : cardChargeBase
        : selectedInstallmentTotal
      : balanceDue;
  // Payment method kontrolü - hem object hem string formatını destekle
  const hasCardPayment = invoiceData?.payment_methods?.some(m => {
    if (typeof m === 'string') return m === 'card' || m === 'credit_card';
    return m?.key === 'credit_card' || m?.key === 'card';
  });
  const hasBankTransfer = invoiceData?.payment_methods?.some(m => {
    if (typeof m === 'string') return m === 'bank' || m === 'bank_transfer';
    return m?.key === 'bank_transfer' || m?.key === 'bank';
  });

  const invoiceCurrency = invoice?.currency as CurrencyForFormat | undefined;
  const formatMoney = (amount: number) => formatCurrencyAmount(amount, invoiceCurrency);

  const statusLabels: Record<string, string> = {
    draft: t('payment.status.draft', 'Draft'),
    pending: t('payment.status.pending', 'Pending'),
    paid: t('payment.status.paid', 'Paid'),
    partially_paid: t('payment.status.partially_paid', 'Partially Paid'),
    overdue: t('payment.status.overdue', 'Overdue'),
    cancelled: t('payment.status.cancelled', 'Cancelled'),
  };

  // Handle payment method selection (tab switch)
  const handlePaymentMethodSelect = (method: 'card' | 'bank') => {
    setPaymentMethod(method);
    setError(null); // Clear any previous errors
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-5 gap-6">
          {/* Payment Form - Sol alan daha geniş */}
          <div className="md:col-span-3">
            <div className="bg-white rounded-lg shadow-lg border border-neutral-200/90 p-6">
              {/* Fatura bilgisi yoksa sadece form; ödeme sekmeleri gizli (Garanti müşteri bilgisi için zorunlu) */}
              {showOnlyBillingForm ? (
                /* Fatura bilgisi yok, kayıtlı adres de yok - sadece form */
                <div>
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                      {error.includes('BillingAddress') || error.includes('billing_address')
                        ? t('payment.error.billing_address_required', 'BillingAddress is required')
                        : error}
                    </div>
                  )}
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h3 className="text-base font-semibold text-amber-900 mb-1.5">
                      {t('payment.billing_info.title', 'Complete Billing Information')}
                    </h3>
                    <p className="text-sm text-amber-800 mb-4">
                      {t('payment.billing_info.description', 'Please complete your billing information before proceeding with the payment.')}
                    </p>
                    <form onSubmit={handleBillingInfoSubmit} className="space-y-3">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-medium text-gray-700">
                          {t('payment.billing_info.type', 'Invoice Type')} <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="billing_type"
                              value="individual"
                              checked={billingType === 'individual'}
                              onChange={() => setBillingType('individual')}
                              className="w-3.5 h-3.5 text-alsero-first focus:ring-alsero-first"
                            />
                            <span className="text-xs text-gray-700">{t('payment.billing_info.individual', 'Individual')}</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="billing_type"
                              value="corporate"
                              checked={billingType === 'corporate'}
                              onChange={() => setBillingType('corporate')}
                              className="w-3.5 h-3.5 text-alsero-first focus:ring-alsero-first"
                            />
                            <span className="text-xs text-gray-700">{t('payment.billing_info.corporate', 'Corporate')}</span>
                          </label>
                        </div>
                      </div>

                      {billingType === 'individual' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              {t('payment.billing_info.first_name', 'First Name')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={billingFirstName}
                              onChange={(e) => setBillingFirstName(e.target.value)}
                              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm"
                              placeholder={t('payment.billing_info.first_name_placeholder', 'First name')}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              {t('payment.billing_info.last_name', 'Last Name')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={billingLastName}
                              onChange={(e) => setBillingLastName(e.target.value)}
                              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm"
                              placeholder={t('payment.billing_info.last_name_placeholder', 'Last name')}
                            />
                          </div>
                          {billingCountry === 'TR' && (
                            <div className="sm:col-span-2">
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                {t('payment.billing_info.tc_no', 'TC Identity Number')} <span className="text-gray-400 text-[10px]">({t('payment.billing_info.optional', 'optional')})</span>
                              </label>
                              <input
                                type="text"
                                value={billingTcNo}
                                onChange={(e) => setBillingTcNo(e.target.value.replace(/\D/g, '').slice(0, 11))}
                                className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm"
                                placeholder={t('payment.billing_info.tc_no_placeholder', '11 digits')}
                                maxLength={11}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {billingType === 'corporate' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              {t('payment.billing_info.company_name', 'Company Name')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={billingCompanyName}
                              onChange={(e) => setBillingCompanyName(e.target.value)}
                              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm"
                              placeholder={t('payment.billing_info.company_name_placeholder', 'Company name')}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              {t('payment.billing_info.tax_number', 'Tax Number (VKN)')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={billingTaxNumber}
                              onChange={(e) => setBillingTaxNumber(e.target.value)}
                              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm"
                              placeholder={t('payment.billing_info.tax_number_placeholder', 'VKN')}
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              {t('payment.billing_info.tax_office', 'Tax Office')} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={billingTaxOffice}
                              onChange={(e) => setBillingTaxOffice(e.target.value)}
                              className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm"
                              placeholder={t('payment.billing_info.tax_office_placeholder', 'Tax office name')}
                            />
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {t('payment.billing_info.address', 'Address')} {missingFields.includes('address') && <span className="text-red-500">*</span>}
                        </label>
                        <input
                          type="text"
                          value={billingAddress}
                          onChange={(e) => setBillingAddress(e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm"
                          placeholder={t('payment.billing_info.address_placeholder', 'Street, building no.')}
                        />
                      </div>
                      <div>
                        <CountryCitySelect
                          countries={countries}
                          countryValue={billingCountry || null}
                          cityValue={billingCity || null}
                          onCountryChange={(_, countryCode) => {
                            setBillingCountry(countryCode || '');
                            if (countryCode !== 'TR') setBillingTcNo('');
                          }}
                          onCityChange={(_, cityName) => setBillingCity(cityName || '')}
                          countryPlaceholder={t('payment.billing_info.country_placeholder', 'Select Country')}
                          cityPlaceholder={t('payment.billing_info.city_placeholder', 'Select City')}
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={billingSubmitting}
                        className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-medium rounded-lg text-sm"
                      >
                        {billingSubmitting ? t('payment.billing_info.saving', 'Saving...') : t('payment.billing_info.submit', 'Save and Continue')}
                      </button>
                    </form>
                  </div>
                </div>
              ) : (
                <>
              {/* Kayıtlı adres varsa: Select ile seç + ödeme yöntemleri görünür */}
              {showBillingAddressSelect && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h3 className="text-base font-semibold text-amber-900 mb-2">
                    {t('payment.billing_info.select_address', 'Select Billing Address')}
                  </h3>
                  <p className="text-sm text-amber-800 mb-3">
                    {t('payment.billing_info.select_address_help', 'Choose a saved address or enter new details below.')}
                  </p>
                  <div className="flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[200px]">
                      <select
                        value={selectedBillingAddressId}
                        onChange={(e) => setSelectedBillingAddressId(e.target.value ? Number(e.target.value) : '')}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      >
                        <option value="">{t('payment.billing_info.select_placeholder', 'Select an address...')}</option>
                        {customerBillingAddresses.map((addr: { id: number; type: string; title?: string; first_name?: string; last_name?: string; company_name?: string; address?: string; city?: string; country?: string }) => (
                          <option key={addr.id} value={addr.id}>
                            {addr.title || (addr.type === 'corporate' ? addr.company_name : `${addr.first_name || ''} ${addr.last_name || ''}`.trim()) || `${addr.address || ''}, ${addr.city || ''}` || `Address #${addr.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={handleApplySelectedBillingAddress}
                      disabled={!selectedBillingAddressId || billingSubmitting}
                      className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-medium rounded-lg text-sm"
                    >
                      {billingSubmitting ? t('payment.billing_info.saving', 'Saving...') : t('payment.billing_info.apply', 'Apply')}
                    </button>
                  </div>
                </div>
              )}
              {/* Tabs Header - Fatura bilgisi tamamlandıysa veya adres seçilebilir durumdaysa göster */}
              <div className="flex border-b border-gray-200 mb-6">
                {/* Credit Card Tab */}
                {hasCardPayment && (
                  <button type="button"
                    onClick={() => handlePaymentMethodSelect('card')}
                    className={`flex-1 px-6 py-4 text-center border-b-2 transition-colors ${
                      paymentMethod === 'card'
                        ? 'border-blue-600 text-blue-600 font-semibold'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-center mb-2">
                      <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <span className="text-base font-medium">
                        {t('payment.card', 'Credit/Debit Card')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {t('payment.secure', 'Secure with 3D Secure')}
                    </p>
                  </button>
                )}

                {/* Bank Transfer Tab */}
                {hasBankTransfer && (
                  <button type="button"
                    onClick={() => handlePaymentMethodSelect('bank')}
                    className={`flex-1 px-6 py-4 text-center border-b-2 transition-colors ${
                      paymentMethod === 'bank'
                        ? 'border-green-600 text-green-600 font-semibold'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-center mb-2">
                      <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M10.5 3L12 2l1.5 1M3 10l9-7 9 7" />
                      </svg>
                      <span className="text-base font-medium">
                        {t('payment.bank_transfer', 'Bank Transfer')}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {t('payment.manual_approval', 'Manual approval required')}
                    </p>
                  </button>
                )}
              </div>

              {paymentSchedule.length > 0 && (
                <div className="mb-6 rounded-lg border border-amber-100 bg-amber-50/50 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    {t('payment.schedule.title', 'Payment schedule')}
                  </h3>
                  <ul className="space-y-2 text-sm">
                    {paymentSchedule.map((row) => (
                      <li key={row.id} className="flex flex-wrap items-center justify-between gap-2">
                        <span>
                          {row.due_date
                            ? new Date(row.due_date).toLocaleDateString(currentLocale === 'tr' ? 'tr-TR' : 'en-GB')
                            : '—'}{' '}
                          —{' '}
                          {formatMoney(row.total_due ?? row.amount)}
                        </span>
                        {row.payment_url && row.status !== 'completed' && (
                          <a href={row.payment_url} className="text-blue-600 hover:underline text-xs font-medium" target="_blank" rel="noopener noreferrer">
                            {t('payment.schedule.pay_due', 'Pay this due')}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tab Content */}
              <div>
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex flex-wrap items-center justify-between gap-2">
                    <span>
                      {error.includes('BillingAddress') || error.includes('billing_address')
                        ? t('payment.error.billing_address_required', 'BillingAddress is required')
                        : error}
                    </span>
                    {hasCardPayment && (
                      <button
                        type="button"
                        onClick={() => { setError(null); setPaymentMethod('card'); }}
                        className="shrink-0 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        {t('payment.try_again', 'Try again')}
                      </button>
                    )}
                  </div>
                )}

                  {/* Card Payment Form – merkezi PaymentCardForm bileşeni */}
                  {paymentMethod === 'card' && (
                    <form onSubmit={handleCardPayment} className="space-y-6">
                      {partialOffered && (
                        <label className="flex cursor-pointer items-start gap-2 text-sm text-gray-800">
                          <input
                            type="checkbox"
                            checked={wantsPartialPayment}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setWantsPartialPayment(checked);
                              if (!checked) {
                                setPayNowAmount('');
                              } else if (partialPaymentContext?.bounds) {
                                const suggested = partialPaymentContext.suggested_pay_amount;
                                const min = partialPaymentContext.bounds.min_amount;
                                setPayNowAmount(
                                  typeof suggested === 'number' && suggested > 0
                                    ? String(suggested)
                                    : String(min),
                                );
                              }
                            }}
                            className="mt-0.5 h-4 w-4 rounded border-gray-400 text-alsero-first focus:ring-alsero-first"
                          />
                          <span>
                            {t(
                              'payment.partial.opt_in',
                              currentLocale === 'tr' ? 'Kısmi ödemek istiyorum' : 'I want to pay partially',
                            )}
                          </span>
                        </label>
                      )}
                      {partialBounds && (
                        <PartialPaymentAmountBlock
                          bounds={partialBounds}
                          currencyCode={invoice?.currency?.code ?? 'TRY'}
                          currencySymbol={invoice?.currency?.symbol ?? '₺'}
                          value={payNowAmount}
                          onChange={setPayNowAmount}
                          t={t}
                          language={currentLocale === 'tr' ? 'tr' : 'en'}
                        />
                      )}
                      <PaymentCardForm
                        value={{
                          cardHolder,
                          cardNumber,
                          expireMonth,
                          expireYear,
                          cvc,
                          use3D,
                        }}
                        onChange={(next) => {
                          if (next.cardHolder !== undefined) setCardHolder(next.cardHolder);
                          if (next.cardNumber !== undefined) setCardNumber(next.cardNumber);
                          if (next.expireMonth !== undefined) setExpireMonth(next.expireMonth);
                          if (next.expireYear !== undefined) setExpireYear(next.expireYear);
                          if (next.cvc !== undefined) setCvc(next.cvc);
                          if (next.use3D !== undefined) setUse3D(next.use3D);
                        }}
                        t={t}
                        testMode={!!invoiceData?.test_mode}
                        paymentProviderKey={invoiceData?.payment_provider_key}
                        onLoadTestCard={invoiceData?.test_mode ? loadTestCard : undefined}
                        showCardAnimation
                        installments={installments}
                        installment={installment}
                        onInstallmentChange={setInstallment}
                        installmentSectionTitle={t('payment.bank_installment', 'Bank installment')}
                        currencySymbol={invoice?.currency.symbol ?? '₺'}
                      />
                      <button
                        type="submit"
                        disabled={processing}
                        className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {processing ? t('payment.processing', '') : `${formatMoney(payableAmount)} ${t('payment.pay', '')}`}
                      </button>
                    </form>
                  )}

                  {/* Bank Transfer Form – merkezi PaymentBankTransferForm bileşeni */}
                  {paymentMethod === 'bank' && (
                    <PaymentBankTransferForm
                      value={{
                        selectedBank,
                        transferAmount,
                        transferDate,
                        senderName,
                        senderBank,
                        referenceNo,
                        senderEmail,
                        senderPhone,
                        notes,
                        receiptFile,
                      }}
                      onChange={(next) => {
                        if (next.selectedBank !== undefined) setSelectedBank(next.selectedBank);
                        if (next.transferAmount !== undefined) setTransferAmount(next.transferAmount);
                        if (next.transferDate !== undefined) setTransferDate(next.transferDate);
                        if (next.senderName !== undefined) setSenderName(next.senderName);
                        if (next.senderBank !== undefined) setSenderBank(next.senderBank);
                        if (next.referenceNo !== undefined) setReferenceNo(next.referenceNo);
                        if (next.senderEmail !== undefined) setSenderEmail(next.senderEmail);
                        if (next.senderPhone !== undefined) setSenderPhone(next.senderPhone);
                        if (next.notes !== undefined) setNotes(next.notes);
                        if (next.receiptFile !== undefined) setReceiptFile(next.receiptFile);
                      }}
                      bankAccounts={bankAccounts}
                      t={t}
                      onSubmit={handleBankTransfer}
                      processing={processing}
                      uploadingReceipt={uploadingReceipt}
                      maxAmount={invoiceData?.invoice?.remaining_amount}
                      minAmount={partialBounds?.min_amount}
                    />
                  )}
              </div>
                </>
              )}
            </div>
          </div>

          {/* Invoice Summary */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-lg shadow-lg border border-neutral-200/90 p-6">
              <h2 className="text-lg font-semibold mb-4">{t('payment.invoice_info', 'Invoice Information')}</h2>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('payment.invoice_no', 'Invoice No')}:</span>
                  <span className="font-medium">{invoice?.invoice_no}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('payment.status', 'Status')}:</span>
                  <span className={`px-2 py-1 rounded text-xs ${statusColors[invoice?.status || 'pending']}`}>
                    {statusLabels[invoice?.status || 'pending']}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('payment.customer', 'Customer')}:</span>
                  <span className="font-medium">{invoice?.customer_name}</span>
                </div>
                {invoice?.reservation_summary && (
                  <>
                    {invoice.reservation_summary.product_name && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('reservation.tour_summary', 'Product')}:</span>
                        <span className="font-medium">{invoice.reservation_summary.product_name}</span>
                      </div>
                    )}
                    {invoice.reservation_summary.package_name && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('reservation.package', 'Package')}:</span>
                        <span className="font-medium uppercase">{invoice.reservation_summary.package_name}</span>
                      </div>
                    )}
                    {invoice.reservation_summary.is_tour_or_ship && invoice.reservation_summary.travel_date && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('payment.travel_date', 'Date')}:</span>
                        <span>{invoice.reservation_summary.travel_date}</span>
                      </div>
                    )}
                    {!invoice.reservation_summary.is_tour_or_ship && (invoice.reservation_summary.check_in_date || invoice.reservation_summary.check_out_date) && (
                      <>
                        {invoice.reservation_summary.check_in_date && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">{t('payment.check_in', 'Check-in')}:</span>
                            <span>{invoice.reservation_summary.check_in_date}</span>
                          </div>
                        )}
                        {invoice.reservation_summary.check_out_date && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">{t('payment.check_out', 'Check-out')}:</span>
                            <span>{invoice.reservation_summary.check_out_date}</span>
                          </div>
                        )}
                      </>
                    )}
                    {(invoice.reservation_summary.rooms_guest_summary?.length ?? 0) > 0 ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="text-gray-600">{t('reservation.guest_details_by_room', 'Guests (per room)')}:</span>
                        {invoice.reservation_summary.rooms_guest_summary!.map((item, i) => {
                          const line = typeof item === 'string'
                            ? item
                            : `${t('reservation.room_number', 'Room')} ${item.room_number}: ${item.adults} ${t('reservation.adults', 'adults')}${item.children > 0 ? `, ${item.children} ${t('reservation.children', 'children')}` : ''}`;
                          return <span key={i} className="text-gray-800 text-xs">{line}</span>;
                        })}
                      </div>
                    ) : invoice.reservation_summary.guest_count != null && invoice.reservation_summary.guest_count > 0 ? (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('reservation.guest_count', 'Guests')}:</span>
                        <span>{invoice.reservation_summary.guest_count}</span>
                      </div>
                    ) : null}
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('payment.issue_date', 'Issue Date')}:</span>
                  <span>{invoice?.issue_date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('payment.due_date', 'Due Date')}:</span>
                  <span>{invoice?.due_date}</span>
                </div>

                <hr className="my-3" />

                {/* Items – sağ barda ürün adı tekrarı kaldırılmış kısa etiket (fatura/PDF tam açıklama kullanır) */}
                <div className="space-y-3">
                  {invoice?.items.map((item, idx) => (
                    <div key={idx} className="text-xs border-b border-gray-100 pb-2 last:border-0">
                      <div className="flex justify-between gap-2 font-medium items-start">
                        <span className="text-gray-700 min-w-0 flex-1 break-words">
                          {getSidebarItemLabel(item.description, invoice?.reservation_summary?.product_name)}
                        </span>
                        <span className="shrink-0 whitespace-nowrap">{formatMoney(item.total_amount)}</span>
                      </div>
                      <div className="text-gray-400 mt-1 whitespace-nowrap">
                        {item.quantity} x {formatMoney(item.unit_price)}
                      </div>
                      {item.discount_amount > 0 && (
                        <div className="text-green-600 whitespace-nowrap">
                          {t('payment.discount', 'Discount')} 
                          {item.discount_type === 'percentage' ? ` (%${item.discount_value})` : ''}: 
                          -{formatMoney(item.discount_amount)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <hr className="my-3" />

                {/* Subtotal - Before discount */}
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('payment.subtotal', 'Subtotal')}:</span>
                  <span className="whitespace-nowrap">
                    {formatMoney(invoice?.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0) || 0)}
                  </span>
                </div>

                {/* Total Discount */}
                {(invoice?.items.reduce((sum, item) => sum + (item.discount_amount || 0), 0) || 0) > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>{t('payment.total_discount', 'Total Discount')}:</span>
                    <span className="whitespace-nowrap">-{formatMoney(invoice?.items.reduce((sum, item) => sum + (item.discount_amount || 0), 0) || 0)}</span>
                  </div>
                )}

                {(invoice?.tax_amount || 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('payment.tax', 'VAT')}:</span>
                    <span className="whitespace-nowrap">{formatMoney(invoice?.tax_amount ?? 0)}</span>
                  </div>
                )}

                <div className="flex justify-between font-bold text-lg">
                  <span>{t('payment.invoice_total', 'Invoice total')}:</span>
                  <span className="whitespace-nowrap">{formatMoney(invoice?.total_amount ?? 0)}</span>
                </div>
                {(invoice?.paid_amount || 0) > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>{t('payment.paid', 'Paid')}:</span>
                    <span className="whitespace-nowrap">-{formatMoney(invoice?.paid_amount ?? 0)}</span>
                  </div>
                )}
                {balanceDue > 0 && (
                  <div className="flex justify-between font-semibold text-blue-700">
                    <span>{t('payment.balance_due', 'Balance due')}:</span>
                    <span className="whitespace-nowrap">{formatMoney(balanceDue)}</span>
                  </div>
                )}

                {paymentMethod === 'card' && cardCommissionAmount > 0 && (
                  <div className="flex justify-between text-amber-700">
                    <span>
                      {t('payment.card_commission', 'Card commission')}
                      {cardCommissionRate > 0 ? ` (${cardCommissionRate}%)` : ''}:
                    </span>
                    <span className="whitespace-nowrap">+{formatMoney(cardCommissionAmount)}</span>
                  </div>
                )}

                {paymentMethod === 'card' && payableAmount > 0 && (
                  <div className="flex justify-between font-bold text-emerald-700 mt-2 pt-2 border-t-2 border-emerald-200 bg-emerald-50/50 -mx-3 px-3 py-2 rounded">
                    <span>{t('payment.total_with_card', 'Total with card')}:</span>
                    <span className="whitespace-nowrap">{formatMoney(payableAmount)}</span>
                  </div>
                )}

                {hasBankTransfer && cardCommissionAmount > 0 && (
                  <p className="text-xs text-gray-600 mt-2 pt-2 border-t border-gray-100 leading-relaxed">
                    {t('payment.bank_transfer_savings_hint', 'Pay by bank transfer and save {amount} ({rate}%) in card fees.')
                      .replace('{amount}', formatMoney(cardCommissionAmount))
                      .replace('{rate}', String(cardCommissionRate))}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
